import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Radar, BellRing, BarChart3, Hexagon, Wrench, ClipboardList, MessageSquareWarning, Recycle, CreditCard, ReceiptText, FileWarning, FolderSearch } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import FleetMap from "../components/FleetMap";

const TONES = {
  "Aktif": "bg-[#198754] hover:bg-[#198754]",
  "Selesai": "bg-[#10056E] hover:bg-[#10056E]",
  "Dibayar": "bg-[#198754] hover:bg-[#198754]",
  "Dipadankan": "bg-[#198754] hover:bg-[#198754]",
  "Normal": "bg-[#198754] hover:bg-[#198754]",
  "Kekal": "bg-[#198754] hover:bg-[#198754]",
  "Disemak": "bg-[#10056E] hover:bg-[#10056E]",
  "Dihala": "bg-sky-600 hover:bg-sky-600",
  "Dijadualkan": "bg-sky-600 hover:bg-sky-600",
  "Dalam Proses": "bg-sky-600 hover:bg-sky-600",
  "Dalam Pembaikan": "bg-sky-600 hover:bg-sky-600",
  "Baharu": "bg-slate-500 hover:bg-slate-500",
  "Terbuka": "bg-slate-500 hover:bg-slate-500",
  "Tertunggak": "bg-[#DC3545] hover:bg-[#DC3545]",
  "Kritikal": "bg-[#DC3545] hover:bg-[#DC3545]",
  "Rendah": "bg-[#DC3545] hover:bg-[#DC3545]",
  "Lupus": "bg-[#DC3545] hover:bg-[#DC3545]",
  "Tidak Dipadankan": "bg-[#DC3545] hover:bg-[#DC3545]",
};

const MODULES = {
  "peta-langsung": {
    icon: MapPin, group: "GPS & Penjejakan", title: "Peta Langsung",
    desc: "Pantau lokasi masa nyata seluruh fleet di atas peta interaktif.",
    map: true,
    stats: [
      { label: "Kenderaan Dalam Talian", value: "4" },
      { label: "Sedang Bergerak", value: "1" },
      { label: "Enjin Melahu", value: "1" },
      { label: "Melebihi Had Laju", value: "0" },
    ],
  },
  "penjejakan": {
    icon: Radar, group: "GPS & Penjejakan", title: "Penjejakan Kenderaan",
    desc: "Jejak pergerakan setiap kenderaan — laluan, hentian dan tempoh melahu.",
    map: true,
    stats: [
      { label: "Jumlah Jarak Hari Ini", value: "214 km" },
      { label: "Purata Kelajuan", value: "46 km/j" },
      { label: "Jumlah Hentian", value: "12" },
      { label: "Masa Melahu", value: "1j 24m" },
    ],
  },
  "amaran": {
    icon: BellRing, group: "GPS & Penjejakan", title: "Amaran",
    desc: "Amaran automatik untuk pemanduan agresif, kelajuan dan pelanggaran geofence.",
    stats: [
      { label: "Amaran Hari Ini", value: "6" },
      { label: "Kelajuan Berlebihan", value: "2" },
      { label: "Brek Kasar", value: "3" },
      { label: "Luar Geofence", value: "1" },
    ],
    chart: { title: "Amaran 7 Hari Terakhir", type: "bar", data: [{ label: "Isn", value: 4 }, { label: "Sel", value: 7 }, { label: "Rab", value: 3 }, { label: "Kha", value: 5 }, { label: "Jum", value: 8 }, { label: "Sab", value: 2 }, { label: "Ahd", value: 6 }] },
    table: {
      title: "Amaran Terkini",
      columns: ["Masa", "Kenderaan", "Jenis Amaran", "Lokasi", "Status"],
      rows: [
        ["09:42", "VBC 5678", "Kelajuan Berlebihan (112 km/j)", "LDP, Petaling Jaya", "Baharu"],
        ["09:15", "WXY 1234", "Brek Kasar", "Jalan Duta, KL", "Baharu"],
        ["08:50", "VBC 5678", "Pecutan Mengejut", "NKVE, Shah Alam", "Disemak"],
        ["08:12", "JJK 3456", "Keluar Geofence", "Depoh Kepong", "Disemak"],
        ["07:45", "WXY 1234", "Brek Kasar", "Jalan Ipoh, KL", "Disemak"],
      ],
    },
  },
  "analitik-fleet": {
    icon: BarChart3, group: "GPS & Penjejakan", title: "Analitik Fleet",
    desc: "Analitik prestasi fleet — penggunaan, kos operasi dan skor staf.",
    stats: [
      { label: "Skor Fleet", value: "82/100" },
      { label: "Kos / KM", value: "RM 0.42" },
      { label: "Penggunaan Purata", value: "63%" },
      { label: "CO₂ Dianggarkan", value: "1.2 tan" },
    ],
    chart: { title: "Skor Fleet Mingguan", type: "line", data: [{ label: "M1", value: 76 }, { label: "M2", value: 79 }, { label: "M3", value: 74 }, { label: "M4", value: 82 }] },
    table: {
      title: "Prestasi Mengikut Kenderaan",
      columns: ["Kenderaan", "Skor", "Kos/KM", "Penggunaan", "Status"],
      rows: [
        ["WXY 1234", "88", "RM 0.38", "72%", "Normal"],
        ["VBC 5678", "74", "RM 0.51", "65%", "Normal"],
        ["JJK 3456", "91", "RM 0.35", "58%", "Normal"],
        ["QAA 9012", "62", "RM 0.63", "41%", "Kritikal"],
      ],
    },
  },
  "geofences": {
    icon: Hexagon, group: "GPS & Penjejakan", title: "Geofences",
    desc: "Sempadan maya untuk depoh, kawasan operasi dan zon larangan.",
    stats: [
      { label: "Geofence Aktif", value: "5" },
      { label: "Pelanggaran Bulan Ini", value: "3" },
      { label: "Kenderaan Dalam Zon", value: "2" },
      { label: "Zon Larangan", value: "1" },
    ],
    table: {
      title: "Senarai Geofence",
      columns: ["Nama Zon", "Jenis", "Kenderaan Dalam Zon", "Pelanggaran", "Status"],
      rows: [
        ["Depoh Utama Kepong", "Depoh", "2", "0", "Aktif"],
        ["Kawasan Operasi KL", "Operasi", "1", "1", "Aktif"],
        ["Zon Putrajaya", "Operasi", "0", "0", "Aktif"],
        ["Kawasan Larangan Pelabuhan", "Larangan", "0", "2", "Aktif"],
        ["Depoh Sokongan Shah Alam", "Depoh", "1", "0", "Aktif"],
      ],
    },
  },
  "jadual-pm": {
    icon: Wrench, group: "Penyelenggaraan", title: "Jadual Penyelenggaraan (PM)",
    desc: "Penyelenggaraan pencegahan berdasarkan jarak, masa atau jam enjin.",
    stats: [
      { label: "PM Tertunggak", value: "2" },
      { label: "PM Bulan Ini", value: "5" },
      { label: "Patuh Jadual", value: "87%" },
      { label: "Kos Purata Servis", value: "RM 480" },
    ],
    chart: { title: "PM Selesai Mengikut Bulan", type: "bar", data: [{ label: "Feb", value: 4 }, { label: "Mac", value: 6 }, { label: "Apr", value: 3 }, { label: "Mei", value: 7 }, { label: "Jun", value: 5 }] },
    table: {
      title: "Jadual PM Akan Datang",
      columns: ["Kenderaan", "Jenis Servis", "Tarikh Akhir", "Odometer Sasaran", "Status"],
      rows: [
        ["QAA 9012", "Servis Major 90,000 km", "28 Jun 2026", "90,000 km", "Tertunggak"],
        ["VBC 5678", "Tukar Minyak Enjin", "2 Jul 2026", "65,000 km", "Tertunggak"],
        ["WXY 1234", "Servis Berkala 50,000 km", "15 Jul 2026", "50,000 km", "Dijadualkan"],
        ["JJK 3456", "Penjajaran & Imbangan Tayar", "22 Jul 2026", "35,000 km", "Dijadualkan"],
        ["VBC 5678", "Pemeriksaan Brek", "1 Ogo 2026", "66,000 km", "Dijadualkan"],
      ],
    },
  },
  "rekod-servis": {
    icon: ClipboardList, group: "Penyelenggaraan", title: "Rekod Servis",
    desc: "Sejarah penuh servis dan pembaikan setiap kenderaan.",
    stats: [
      { label: "Rekod Servis", value: "48" },
      { label: "Kos Tahun Ini", value: "RM 23,450" },
      { label: "Bengkel Panel", value: "6" },
      { label: "Dalam Bengkel", value: "1" },
    ],
    table: {
      title: "Rekod Servis Terkini",
      columns: ["Tarikh", "Kenderaan", "Servis", "Bengkel", "Kos (RM)", "Status"],
      rows: [
        ["20 Jun 2026", "QAA 9012", "Baik pulih sistem penghawa dingin", "AutoFix Kepong", "1,250.00", "Dalam Proses"],
        ["12 Jun 2026", "WXY 1234", "Servis berkala 45,000 km", "Proton Edar Servis", "380.00", "Selesai"],
        ["5 Jun 2026", "VBC 5678", "Tukar bateri & pemeriksaan alternator", "AutoFix Kepong", "560.00", "Selesai"],
        ["28 Mei 2026", "JJK 3456", "Tukar tayar hadapan (2)", "Tayaria Jalan Ipoh", "720.00", "Selesai"],
        ["15 Mei 2026", "QAA 9012", "Servis enjin diesel", "Hino Service Centre", "1,840.00", "Selesai"],
      ],
    },
  },
  "aduan": {
    icon: MessageSquareWarning, group: "Penyelenggaraan", title: "Aduan Kerosakan",
    desc: "Staf melapor kerosakan; pengendali menghala aduan ke bengkel.",
    stats: [
      { label: "Aduan Terbuka", value: "3" },
      { label: "Dalam Pembaikan", value: "1" },
      { label: "Selesai Bulan Ini", value: "7" },
      { label: "Purata Masa Baiki", value: "2.4 hari" },
    ],
    table: {
      title: "Aduan Terkini",
      columns: ["Tarikh", "Kenderaan", "Aduan", "Staf", "Keutamaan", "Status"],
      rows: [
        ["24 Jun 2026", "QAA 9012", "Bunyi berdecit pada brek belakang", "Rahman", "Kritikal", "Dalam Pembaikan"],
        ["23 Jun 2026", "VBC 5678", "Lampu amaran enjin menyala", "Rahman", "Kritikal", "Terbuka"],
        ["22 Jun 2026", "WXY 1234", "Penghawa dingin kurang sejuk", "Rahman", "Normal", "Terbuka"],
        ["20 Jun 2026", "JJK 3456", "Cermin sisi kiri retak", "Rahman", "Normal", "Terbuka"],
        ["15 Jun 2026", "WXY 1234", "Wiper tidak berfungsi", "Rahman", "Normal", "Selesai"],
      ],
    },
  },
  "pelupusan": {
    icon: Recycle, group: "Penyelenggaraan", title: "Analitik Pelupusan (End-of-Life)",
    desc: "Analisis hayat aset — kos kumulatif vs nilai pasaran untuk cadangan pelupusan.",
    stats: [
      { label: "Aset Kritikal", value: "1" },
      { label: "Cadangan Lupus", value: "1" },
      { label: "Nilai Baki Fleet", value: "RM 186k" },
      { label: "Umur Purata Fleet", value: "6.2 thn" },
    ],
    chart: { title: "Skor Kesihatan Aset", type: "bar", data: [{ label: "WXY 1234", value: 84 }, { label: "VBC 5678", value: 71 }, { label: "JJK 3456", value: 90 }, { label: "QAA 9012", value: 38 }] },
    table: {
      title: "Penilaian Hayat Aset",
      columns: ["Kenderaan", "Umur", "Kos Kumulatif", "Nilai Pasaran", "Skor Kesihatan", "Cadangan"],
      rows: [
        ["QAA 9012", "11 thn", "RM 48,200", "RM 22,000", "38/100", "Lupus"],
        ["VBC 5678", "7 thn", "RM 18,900", "RM 41,000", "71/100", "Kekal"],
        ["WXY 1234", "4 thn", "RM 9,400", "RM 38,000", "84/100", "Kekal"],
        ["JJK 3456", "3 thn", "RM 5,100", "RM 52,000", "90/100", "Kekal"],
      ],
    },
  },
  "aset-tol": {
    icon: CreditCard, group: "Tol & Saman", title: "Aset Tol (Touch 'n Go)",
    desc: "Urus kad Touch 'n Go / SmartTAG setiap kenderaan — baki dan tambah nilai.",
    stats: [
      { label: "Kad Aktif", value: "4" },
      { label: "Jumlah Baki", value: "RM 342.50" },
      { label: "Baki Rendah", value: "1" },
      { label: "Tambah Nilai Bulan Ini", value: "RM 500" },
    ],
    table: {
      title: "Kad Tol Berdaftar",
      columns: ["No. Kad", "Kenderaan", "Baki (RM)", "Tambah Nilai Terakhir", "Status"],
      rows: [
        ["6012 34** **** 8801", "WXY 1234", "128.40", "18 Jun 2026 (RM 100)", "Normal"],
        ["6012 34** **** 8802", "VBC 5678", "96.10", "10 Jun 2026 (RM 150)", "Normal"],
        ["6012 34** **** 8803", "JJK 3456", "104.00", "20 Jun 2026 (RM 100)", "Normal"],
        ["6012 34** **** 8804", "QAA 9012", "14.00", "28 Mei 2026 (RM 150)", "Rendah"],
      ],
    },
  },
  "transaksi-tol": {
    icon: ReceiptText, group: "Tol & Saman", title: "Transaksi Tol",
    desc: "Import dan padankan transaksi tol dengan log perjalanan.",
    stats: [
      { label: "Transaksi Bulan Ini", value: "86" },
      { label: "Jumlah Kos Tol", value: "RM 412.30" },
      { label: "Tidak Dipadankan", value: "4" },
      { label: "Kos / Kenderaan", value: "RM 103" },
    ],
    chart: { title: "Kos Tol Mingguan (RM)", type: "bar", data: [{ label: "M1", value: 88 }, { label: "M2", value: 132 }, { label: "M3", value: 96 }, { label: "M4", value: 96 }] },
    table: {
      title: "Transaksi Terkini",
      columns: ["Masa", "Plaza Tol", "Kenderaan", "Amaun (RM)", "Status"],
      rows: [
        ["25 Jun 2026 09:12", "Jalan Duta (PLUS)", "VBC 5678", "3.50", "Dipadankan"],
        ["25 Jun 2026 08:47", "Damansara (LDP)", "WXY 1234", "2.10", "Dipadankan"],
        ["24 Jun 2026 17:33", "Sungai Besi (Besraya)", "JJK 3456", "1.80", "Tidak Dipadankan"],
        ["24 Jun 2026 14:05", "Batu Tiga (KESAS)", "VBC 5678", "2.20", "Dipadankan"],
        ["24 Jun 2026 09:58", "Jalan Duta (PLUS)", "WXY 1234", "3.50", "Dipadankan"],
      ],
    },
  },
  "saman": {
    icon: FileWarning, group: "Tol & Saman", title: "Penghalaan Saman",
    desc: "Saman dihala automatik kepada staf bertugas berdasarkan buku log.",
    stats: [
      { label: "Saman Terbuka", value: "2" },
      { label: "Telah Dihala", value: "5" },
      { label: "Dibayar", value: "3" },
      { label: "Jumlah Denda", value: "RM 850" },
    ],
    table: {
      title: "Saman Aktif",
      columns: ["Tarikh", "No. Saman", "Kenderaan", "Kesalahan", "Staf", "Status"],
      rows: [
        ["18 Jun 2026", "PDRM-88214", "VBC 5678", "Melebihi had laju (Lebuhraya NKVE)", "Rahman", "Dihala"],
        ["10 Jun 2026", "DBKL-45120", "WXY 1234", "Parkir tanpa kupon", "Rahman", "Terbuka"],
        ["30 Mei 2026", "PDRM-87950", "JJK 3456", "Melanggar lampu isyarat", "Rahman", "Dibayar"],
        ["22 Mei 2026", "JPJ-30281", "QAA 9012", "Kegagalan pemeriksaan berkala", "—", "Terbuka"],
      ],
    },
  },
  "rekod-saman": {
    icon: FolderSearch, group: "Tol & Saman", title: "Rekod Saman",
    desc: "Arkib penuh saman mengikut kenderaan dan staf untuk audit.",
    stats: [
      { label: "Jumlah Rekod", value: "14" },
      { label: "Staf Berulang", value: "2" },
      { label: "Kesalahan Lazim", value: "Had Laju" },
      { label: "Denda Terkumpul", value: "RM 2,150" },
    ],
    chart: { title: "Saman Mengikut Bulan", type: "bar", data: [{ label: "Feb", value: 2 }, { label: "Mac", value: 4 }, { label: "Apr", value: 1 }, { label: "Mei", value: 4 }, { label: "Jun", value: 3 }] },
    table: {
      title: "Arkib Saman",
      columns: ["Tarikh", "Kenderaan", "Kesalahan", "Staf", "Denda (RM)", "Status"],
      rows: [
        ["18 Jun 2026", "VBC 5678", "Melebihi had laju", "Rahman", "300.00", "Dihala"],
        ["10 Jun 2026", "WXY 1234", "Parkir tanpa kupon", "Rahman", "100.00", "Terbuka"],
        ["30 Mei 2026", "JJK 3456", "Melanggar lampu isyarat", "Rahman", "300.00", "Dibayar"],
        ["22 Mei 2026", "QAA 9012", "Kegagalan pemeriksaan berkala", "—", "150.00", "Terbuka"],
        ["15 Apr 2026", "WXY 1234", "Melebihi had laju", "Rahman", "150.00", "Dibayar"],
      ],
    },
  },
};

function Cell({ value }) {
  if (TONES[value]) return <Badge className={TONES[value]}>{value}</Badge>;
  return <span className="text-sm">{value}</span>;
}

export default function ModulePage() {
  const { slug } = useParams();
  const mod = MODULES[slug];
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    if (mod?.map) api.get("/telematics/positions").then((r) => setPositions(r.data.positions)).catch(() => {});
  }, [slug, mod]);

  if (!mod) return <p className="text-sm text-muted-foreground">Modul tidak dijumpai.</p>;
  const Icon = mod.icon;

  return (
    <div className="space-y-6 fade-up" data-testid={`module-page-${slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-[#10056E] flex items-center justify-center shrink-0">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{mod.group}</p>
            <h1 className="text-2xl font-bold tracking-tight">{mod.title}</h1>
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground -mt-3 max-w-2xl">{mod.desc}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {mod.stats.map((s) => (
          <Card key={s.label} className="stat-card border-slate-200 shadow-sm rounded-xl">
            <CardContent className="p-5">
              <p className="text-2xl font-bold tracking-tight">{s.value}</p>
              <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {mod.map && (
        <>
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Peta Fleet</CardTitle>
              <CardDescription className="text-xs">Kedudukan semasa kenderaan (demo)</CardDescription>
            </CardHeader>
            <CardContent><FleetMap positions={positions} /></CardContent>
          </Card>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b"><p className="text-sm font-semibold">Status Kenderaan Langsung</p></div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/70">
                  <TableHead>Kenderaan</TableHead><TableHead>Model</TableHead><TableHead>Kelajuan</TableHead>
                  <TableHead>Enjin</TableHead><TableHead>Odometer</TableHead><TableHead>Kemas Kini</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {positions.map((p) => (
                  <TableRow key={p.vehicle_id} className="hover:bg-slate-50">
                    <TableCell className="font-semibold text-sm">{p.registration_number}</TableCell>
                    <TableCell className="text-sm">{p.make} {p.model}</TableCell>
                    <TableCell className="text-sm">{p.speed_kmh} km/j</TableCell>
                    <TableCell><Badge className={p.ignition ? "bg-[#198754] hover:bg-[#198754]" : "bg-slate-500 hover:bg-slate-500"}>{p.ignition ? "Hidup" : "Mati"}</Badge></TableCell>
                    <TableCell className="text-sm">{Number(p.odometer).toLocaleString()} km</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(p.last_update).toLocaleTimeString("ms-MY")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <div className={`grid grid-cols-1 gap-6 ${mod.chart && mod.table ? "lg:grid-cols-3" : ""}`}>
        {mod.chart && (
          <Card className="border-slate-200 shadow-sm rounded-xl lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base font-semibold">{mod.chart.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                {mod.chart.type === "line" ? (
                  <LineChart data={mod.chart.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke="#10056E" strokeWidth={2.5} dot={{ r: 3, fill: "#10056E" }} />
                  </LineChart>
                ) : (
                  <BarChart data={mod.chart.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#10056E" radius={[4, 4, 0, 0]} barSize={28} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        {mod.table && (
          <div className={`bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${mod.chart ? "lg:col-span-2" : ""}`}>
            <div className="px-4 py-3 border-b"><p className="text-sm font-semibold">{mod.table.title}</p></div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/70">
                  {mod.table.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {mod.table.rows.map((row, i) => (
                  <TableRow key={i} className="hover:bg-slate-50">
                    {row.map((cell, j) => <TableCell key={j}><Cell value={cell} /></TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 text-sm text-[#10056E]">
        Paparan ini menggunakan <b>data demo</b>. Integrasi langsung modul {mod.title} akan diaktifkan dalam fasa akan datang — buat masa ini, modul <b>Buku Log</b> berfungsi sepenuhnya.
      </div>
    </div>
  );
}
